/**
 * Désignation des référents (§8.2). Un référent n'est pas un utilisateur : il
 * accède par lien/token, comme un évaluateur — d'où l'absence de compte à créer,
 * y compris quand c'est le créateur de la campagne lui-même.
 *
 * DEUX RÉGIMES, selon ce que le référentiel pondère :
 * - **PAMI** : UN SEUL référent décrit les attentes du poste. Il n'y a pas de
 *   consensus à construire, donc rien à gagner à en multiplier. La carte
 *   disparaît dès qu'il est désigné ; c'est le suivi qui porte le remplacement.
 * - **fit culturel** : plusieurs référents, la moyenne fait le référentiel.
 *
 * L'envoi du lien n'est PAS fait ici : il se déclenche depuis la ligne du
 * référent dans le suivi, qui est aussi l'endroit où l'on relance.
 */
import { useState } from "react";
import { useMutation } from "convex/react";
import { ExternalLink, Loader2, Plus, UserCheck } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
} from "@repo/ui";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

type Props = {
  campagneId: Id<"campagnes">;
  /** Ce que le référentiel pondère — pilote le régime mono/multi référent. */
  cible: "pami" | "pc-fit";
  /** Nombre de référents déjà désignés sur la campagne. */
  nbReferents: number;
};

export function ReferentsInviterCard({
  campagneId,
  cible,
  nbReferents,
}: Props) {
  const inviter = useMutation(api.referentiel.campagne.inviterReferents);
  const meDesigner = useMutation(
    api.referentiel.campagne.designerUtilisateurCommeReferent,
  );

  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [enCours, setEnCours] = useState<"moi" | "invitation" | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [monLien, setMonLien] = useState<string | null>(null);

  const referentUnique = cible === "pami";

  // Le lien vient d'être créé : il prend toute la carte. Se désigner puis voir
  // la carte disparaître (référent unique → plus rien à désigner) laisserait
  // l'utilisateur sans le lien qu'il vient d'obtenir.
  if (monLien !== null) {
    return (
      <Card className="border-primary/40 bg-primary/5">
        <CardHeader>
          <CardTitle>Vous êtes le référent de cette campagne</CardTitle>
          <CardDescription>
            Votre accès est créé. Le lien reste disponible dans le suivi
            ci-dessous, colonne « Actions ».
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild>
            <a href={monLien} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              Remplir le référentiel maintenant
            </a>
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Référent unique déjà en place : plus rien à désigner ici. Le remplacer est
  // une action sur LUI, elle vit dans le suivi.
  if (referentUnique && nbReferents > 0) return null;

  const executer = async (
    cle: "moi" | "invitation",
    action: () => Promise<unknown>,
  ) => {
    setErreur(null);
    setEnCours(cle);
    try {
      await action();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setEnCours(null);
    }
  };

  const ajouter = () => {
    if (!nom.trim() && !email.trim()) return;
    return executer("invitation", async () => {
      await inviter({
        campagneId,
        cible,
        referents: [
          {
            nom: nom.trim() || undefined,
            prenom: prenom.trim() || undefined,
            email: email.trim() || undefined,
          },
        ],
      });
      setPrenom("");
      setNom("");
      setEmail("");
    });
  };

  const meDesignerReferent = () =>
    executer("moi", async () => {
      const { lien } = await meDesigner({ campagneId, cible });
      // Désignation faite, mais `APP_EVALUATEUR_PORTAIL_URL` n'est pas
      // configurée côté Convex : le dire plutôt que d'afficher un lien mort.
      if (lien === null) {
        setErreur(
          "Vous êtes bien désigné référent, mais l'URL du portail évaluateur n'est pas configurée : le lien d'accès ne peut pas être construit.",
        );
        return;
      }
      setMonLien(lien);
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {referentUnique ? "Désigner le référent" : "Inviter un référent"}
        </CardTitle>
        {/* Fit-culturel : aucune description. Le seuil de 3 contributions est
            déjà porté là où il agit — l'avertissement de fiabilité sur la version
            du référentiel — et l'idempotence par email n'est pas une consigne
            utile à l'invitation. */}
        {referentUnique && (
          <CardDescription>
            Une seule personne remplit le référentiel PAMI : vous, ou quelqu'un
            d'autre dans l'organisation. Elle indique, pour chacune des 20
            dimensions, le score attendu chez le candidat : bas, moyen, élevé
            ou pas d'attente particulière.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Le cas « c'est moi » d'abord : c'est le plus fréquent, et il n'a
            besoin d'aucune saisie. */}
        <div className="space-y-2">
          <Button onClick={() => void meDesignerReferent()} disabled={enCours !== null}>
            {enCours === "moi" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <UserCheck className="mr-2 h-4 w-4" />
            )}
            Je suis le référent
          </Button>
          <p className="text-xs text-muted-foreground">
            Vous êtes ajouté comme référent avec votre nom et votre email, et
            vous obtenez aussitôt un lien pour remplir le référentiel — sans
            passer par un email.
          </p>
        </div>

        <div className="flex items-center gap-3 text-xs uppercase text-muted-foreground">
          <span className="h-px flex-1 bg-border" />
          ou
          <span className="h-px flex-1 bg-border" />
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Confier le référentiel à quelqu'un</p>
          <div className="flex flex-wrap items-end gap-2">
            <Input
              placeholder="Prénom"
              value={prenom}
              onChange={(e) => setPrenom(e.target.value)}
              className="w-36"
            />
            <Input
              placeholder="Nom"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              className="w-36"
            />
            <Input
              placeholder="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-60"
            />
            <Button
              variant="outline"
              onClick={() => void ajouter()}
              disabled={enCours !== null}
            >
              {enCours === "invitation" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Inviter
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            L'invitation crée l'accès ; le lien part depuis le suivi ci-dessous,
            là où l'on relance.
          </p>
        </div>

        {erreur && <p className="text-sm text-destructive">{erreur}</p>}
      </CardContent>
    </Card>
  );
}
